import produce from 'immer';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { change } from 'redux-form';

import { INQUIRY_TEMPLATE_EDITOR_FORM_NAME } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { SectionAccordion } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/SectionAccordion';
import { ToggleStyleInput } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/inputs';
import {
  SectionColumn,
  SectionRow,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/sections/shared';
import { UiStylableParentComponent } from 'lib/interfaces/ui-components';

const ChildSizes = {
  SHRINK: '0',
  GROW: '1',
};

type NestedChildLayoutStyleProps = {
  parentComponentPreview: UiStylableParentComponent;
  parentComponentPath: string;
  nestedIndex: number;
  styleVariantIndex: number;
  editable: boolean;
};

const NestedChildLayoutStyle = ({
  parentComponentPreview,
  parentComponentPath,
  nestedIndex,
  styleVariantIndex,
  editable,
}: NestedChildLayoutStyleProps) => {
  const dispatch = useDispatch();
  const parentConfigPath = `steps.${parentComponentPath}.style-variants.${styleVariantIndex}.child-sizes`;

  const componentWidthSetting = useMemo(
    () => parentComponentPreview?.styles?.['child-sizes']?.base?.base ?? [],
    [parentComponentPreview],
  );

  const onLayoutToggleChange = useCallback(
    (value: string, _: string) => {
      const newSettings = produce(componentWidthSetting, (draftSettings) => {
        draftSettings[nestedIndex] = parseInt(value);
        return draftSettings;
      });
      dispatch(change(INQUIRY_TEMPLATE_EDITOR_FORM_NAME, parentConfigPath, newSettings));
    },
    [dispatch, parentConfigPath, componentWidthSetting, nestedIndex],
  );

  return (
    <SectionAccordion title='Flex width'>
      <SectionRow>
        <SectionColumn>
          <ToggleStyleInput
            field='child-sizes'
            editable={editable}
            name={`layout-${parentComponentPreview.name}-child-layout-toggle`}
            defaultValue={
              componentWidthSetting[nestedIndex] != null
                ? componentWidthSetting[nestedIndex].toString()
                : ChildSizes.GROW
            }
            options={[
              {
                label: 'Fixed',
                value: ChildSizes.SHRINK,
              },
              {
                label: 'Fill',
                value: ChildSizes.GROW,
              },
            ]}
            onChange={onLayoutToggleChange}
          />
        </SectionColumn>
      </SectionRow>
    </SectionAccordion>
  );
};

export default NestedChildLayoutStyle;
